import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Grid,
  Alert,
} from '@mui/material'
import { BackupConfiguracao, BackupConfiguracaoCreate, backupsApi } from '../../api/backups'
import { Empresa } from '../../types/api'
import CronHelper from './CronHelper'
import { sortByLocale } from '../../utils/array'

const backupSchema = z.object({
  id_empresa: z.number().nullable(),
  nome_configuracao: z.string().min(1, 'Nome é obrigatório'),
  tipo_backup: z.enum(['completo', 'incremental']),
  cron_expression: z.string().min(1, 'Expressão cron é obrigatória'),
  ativo: z.boolean(),
  s3_bucket: z.string().min(1, 'Bucket S3 é obrigatório'),
  s3_prefix: z.string().optional(),
  retention_days: z.number().min(1, 'Retenção deve ser pelo menos 1 dia'),
})

type BackupFormData = z.infer<typeof backupSchema>

interface BackupFormProps {
  open: boolean
  backup: BackupConfiguracao | null
  empresas: Empresa[]
  onClose: () => void
  onSubmit: (data: BackupConfiguracaoCreate) => void
}

const BackupForm: React.FC<BackupFormProps> = ({
  open,
  backup,
  empresas,
  onClose,
  onSubmit,
}) => {
  const empresasOrdenadas = React.useMemo(() => sortByLocale(empresas, (e) => e.nome ?? ''), [empresas])
  const [cronExplanation, setCronExplanation] = useState<string>('')
  const [cronValid, setCronValid] = useState<boolean>(true)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<BackupFormData>({
    resolver: zodResolver(backupSchema),
    defaultValues: {
      id_empresa: null,
      nome_configuracao: '',
      tipo_backup: 'completo',
      cron_expression: '0 2 * * *',
      ativo: true,
      s3_bucket: '',
      s3_prefix: '',
      retention_days: 3,
    },
  })

  const cronExpression = watch('cron_expression')

  // Validar cron quando mudar
  useEffect(() => {
    if (cronExpression) {
      backupsApi
        .validarCron(cronExpression)
        .then((result) => {
          setCronValid(result.valido)
          setCronExplanation(result.explicacao)
        })
        .catch(() => {
          setCronValid(false)
          setCronExplanation('Erro ao validar cron')
        })
    }
  }, [cronExpression])

  // Resetar formulário quando backup mudar
  useEffect(() => {
    if (open) {
      if (backup) {
        reset({
          id_empresa: backup.id_empresa,
          nome_configuracao: backup.nome_configuracao,
          tipo_backup: backup.tipo_backup,
          cron_expression: backup.cron_expression,
          ativo: backup.ativo,
          s3_bucket: backup.s3_bucket,
          s3_prefix: backup.s3_prefix || '',
          retention_days: backup.retention_days,
        })
      } else {
        reset({
          id_empresa: null,
          nome_configuracao: '',
          tipo_backup: 'completo',
          cron_expression: '0 2 * * *',
          ativo: true,
          s3_bucket: '',
          s3_prefix: '',
          retention_days: 3,
        })
      }
    }
  }, [open, backup, reset])

  const handleFormSubmit = (data: BackupFormData) => {
    const submitData: BackupConfiguracaoCreate = {
      ...data,
      configuracao_incremental:
        data.tipo_backup === 'incremental'
          ? {
              metodo: 'wal_archiving',
              wal_retention_days: 7,
            }
          : null,
    }
    onSubmit(submitData)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogTitle>
          {backup ? 'Editar Configuração de Backup' : 'Nova Configuração de Backup'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Empresa</InputLabel>
                  <Select
                    {...register('id_empresa', { valueAsNumber: true })}
                    value={watch('id_empresa') ?? ''}
                    onChange={(e) => setValue('id_empresa', e.target.value === '' ? null : Number(e.target.value))}
                    label="Empresa"
                  >
                  <MenuItem value="">Galatea Web</MenuItem>
                  {empresasOrdenadas.map((empresa) => (
                      <MenuItem key={empresa.id} value={empresa.id}>
                        {empresa.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('nome_configuracao')}
                  label="Nome da Configuração"
                  fullWidth
                  error={!!errors.nome_configuracao}
                  helperText={errors.nome_configuracao?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Backup</InputLabel>
                  <Select
                    {...register('tipo_backup')}
                    value={watch('tipo_backup')}
                    label="Tipo de Backup"
                  >
                    <MenuItem value="completo">Completo</MenuItem>
                    <MenuItem value="incremental">Incremental</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('retention_days', { valueAsNumber: true })}
                  label="Dias de Retenção"
                  type="number"
                  fullWidth
                  error={!!errors.retention_days}
                  helperText={errors.retention_days?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <CronHelper
                  value={cronExpression}
                  onChange={(value) => setValue('cron_expression', value)}
                />
                {cronExplanation && (
                  <Alert severity={cronValid ? 'info' : 'warning'} sx={{ mt: 1 }}>
                    {cronExplanation}
                  </Alert>
                )}
                {errors.cron_expression && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errors.cron_expression.message}
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('s3_bucket')}
                  label="Bucket S3"
                  fullWidth
                  error={!!errors.s3_bucket}
                  helperText={errors.s3_bucket?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('s3_prefix')}
                  label="Prefixo S3 (opcional)"
                  fullWidth
                  error={!!errors.s3_prefix}
                  helperText={errors.s3_prefix?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch {...register('ativo')} checked={watch('ativo')} />}
                  label="Ativo"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained">
            {backup ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default BackupForm

